// The chats on the board (David, 19 Oct 2026: they follow him to every device, phone and computers alike). The board
// keeps every chat and is the copy that counts; this browser keeps each agent's open chat too, to open it at once and
// to send it on if the page closed before it reached the board. Read and written with the user's own session only.

import type { BetaMessageParam } from "@anthropic-ai/sdk/resources/beta/messages/messages";
import type { ChatMeta } from "../../shared/types";
import { api, ApiError } from "../lib/api";
import type { Provider, Usage } from "./config";
import type { MadePicture } from "./pictures";

/** What a chat holds, on the board and in this browser alike. */
export interface Saved {
  messages: BetaMessageParam[];
  tools: Record<string, { status: string; text?: string }>;
  runs: unknown[];
  usage: Usage;
  hosts: string[];
  pictures?: MadePicture[];
  /** its Hands-free switch (Features 4); left out of a chat whose switch follows the default, and of one from before */
  handsFree?: boolean;
  id?: string;
  at?: number;
  /** when its last reply landed, and when its user last had it open */
  replied?: number;
  seen?: number;
  /** the model it was given in its menu, when not its agent's default (Features 4) */
  model?: string;
  /** the board it is on (Fixes 9, 28 Oct): each board shows its own chats. Left out of a chat kept before */
  board?: string | null;
}

/**
 * A chat's words as the board keeps them: the lines a Files board adds to a message's board context (the path of
 * the note open, from the user's computer) stay out. The agent had them when the message was sent; the board
 * keeps no path of the user's files.
 */
const FILES_LINES = /^(?:This is a Files board\b.*|Open in the user's tab now: .*)\n?/gm;
export function keptCopy(body: Saved): Saved {
  // a picture's small preview stays in this browser: the board keeps none of it (Features 1, 20 Oct)
  if (body.messages.some((m) => (m as { previews?: unknown }).previews)) body = { ...body, messages: body.messages.map((m) => ((m as { previews?: unknown }).previews ? (({ previews: _p, ...rest }) => rest)(m as BetaMessageParam & { previews?: unknown }) : m)) as Saved["messages"] };
  // and a picture a tool showed the agent (read_picture) is a word in its place: the board has it already, by its address
  const looked = (b: unknown) => !!b && typeof b === "object" && (b as { type?: unknown }).type === "tool_result" && Array.isArray((b as { content?: unknown }).content) && ((b as { content: { type?: string }[] }).content).some((c) => c?.type === "image");
  if (body.messages.some((m) => Array.isArray(m.content) && m.content.some(looked)))
    body = {
      ...body,
      messages: body.messages.map((m) =>
        Array.isArray(m.content) && m.content.some(looked) ? { ...m, content: m.content.map((b) => (looked(b) ? { ...b, content: (b as { content: { type: string }[] }).content.map((c) => (c.type === "image" ? { type: "text", text: "(The picture was shown here.)" } : c)) } : b)) } : m,
      ) as Saved["messages"],
    };
  if (!JSON.stringify(body.messages).includes("Files board")) return body;
  const clean = (t: string) => (t.startsWith("<board_context>") ? t.replace(FILES_LINES, "") : t);
  return {
    ...body,
    messages: body.messages.map((m) =>
      typeof m.content === "string" ? { ...m, content: clean(m.content) } : { ...m, content: m.content.map((b) => (b.type === "text" ? { ...b, text: clean(b.text) } : b)) },
    ) as Saved["messages"],
  };
}

/** One save of a chat for the board: its words, and what the list says of it. */
export interface ChatSave {
  id: string;
  agent: Provider;
  title: string;
  at: number;
  replied: number;
  seen: number;
  host: string | null;
  board: string | null;
  body: Saved;
}

/**
 * A save the board answered: kept (its list entry), older than the board's copy (stale), deleted (the chat was deleted,
 * here or on another device, and stays so), or not kept after its tries (why).
 */
export type SaveResult =
  | { id: string; ok: true; chat: ChatMeta }
  | { id: string; ok: false; stale: true; chat: ChatMeta }
  | { id: string; ok: false; gone: true }
  | { id: string; ok: false; stale?: false; gone?: false; error: string; code: string };

let heard: (r: SaveResult) => void = () => {};
/** The store hears how each save went: the list takes the board's word for it. */
export function onSaved(fn: (r: SaveResult) => void) {
  heard = fn;
}

const waiting = new Map<string, { save: ChatSave; timer: ReturnType<typeof setTimeout>; tries: number }>();
/** a save the board could not take for want of a link or a moment's trouble is tried again, this much later each time */
const AGAIN = [5_000, 20_000, 60_000, 180_000];
/** what each chat last sent, so a save of nothing new is not sent again, and when the board last took one */
const sent = new Map<string, string>();
const took = new Map<string, number>();

/** When the board last took a save of this chat from this page: a list asked for after that without it means it was deleted. */
export const tookAt = (id: string): number | undefined => took.get(id);

/** The same chat saved again with nothing new in it: its words and times as the board last got them, whenever it was read. */
const mark = (s: ChatSave) => `${s.at}|${s.replied}|${s.title}|${s.host}|${s.board}|${JSON.stringify(s.body).length}`;

/**
 * A chat's save, sent a moment later with whatever came after it in that moment, so a reply's end is one save.
 * One with nothing new since the last that reached the board is not sent.
 */
export function saveSoon(save: ChatSave, wait = 600) {
  if (sent.get(save.id) === mark(save) && !waiting.has(save.id)) return;
  const w = waiting.get(save.id);
  if (w) clearTimeout(w.timer);
  waiting.set(save.id, { save, timer: setTimeout(() => void send(save.id), wait), tries: 0 });
}

async function send(id: string) {
  const w = waiting.get(id);
  if (!w) return;
  clearTimeout(w.timer);
  waiting.delete(id);
  const { save } = w;
  try {
    const r = await api<{ ok: boolean; stale?: boolean; chat: ChatMeta }>(`/api/chats/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: { agent: save.agent, title: save.title, at: save.at, replied: save.replied, seen: save.seen, host: save.host, board: save.board, body: save.body },
    });
    if (r.ok) {
      sent.set(id, mark(save));
      took.set(id, Date.now());
    }
    heard(r.ok ? { id, ok: true, chat: r.chat } : { id, ok: false, stale: true, chat: r.chat });
  } catch (e) {
    const err = e as ApiError;
    if (err.status === 410) return heard({ id, ok: false, gone: true });
    // no link, or the board busy for a moment: the same save goes again, unless a newer one took its place meanwhile
    const again = AGAIN[w.tries];
    if ((err.status === 0 || err.status === 429 || err.status >= 500) && again !== undefined && !waiting.has(id)) {
      waiting.set(id, { save, timer: setTimeout(() => void send(id), again), tries: w.tries + 1 });
      return;
    }
    heard({ id, ok: false, error: err.message, code: err.code ?? "error" });
  }
}

/** Every save waiting, sent now: before a chat is left for good, or the page goes. */
export function saveNow(id?: string): Promise<void> {
  const ids = id ? [id] : [...waiting.keys()];
  return Promise.all(ids.map((x) => send(x))).then(() => undefined);
}

/** Whether a chat has a save not yet on the board. */
export const unsaved = (id: string) => waiting.has(id);

/** The chats on the board, the most recently written first. */
export function listChats(): Promise<ChatMeta[]> {
  // an answer with no list in it is no answer: never read as "no chats", which would let this browser's copies go
  return api<{ chats?: ChatMeta[] }>("/api/chats").then((r) => {
    if (!Array.isArray(r?.chats)) throw new Error("The board did not send your chats.");
    return r.chats;
  });
}

/** A chat's words, from the board. Null when the board no longer has it; a failure to reach the board throws. */
export async function readChat(id: string): Promise<{ chat: ChatMeta; body: Saved } | null> {
  try {
    return await api<{ chat: ChatMeta; body: Saved }>(`/api/chats/${encodeURIComponent(id)}`);
  } catch (e) {
    if ((e as ApiError).status === 404) return null;
    throw e;
  }
}

/** A chat named, pinned or read. */
export function patchChat(id: string, patch: { title?: string; pinned?: boolean; seen?: number }): Promise<ChatMeta> {
  return api<{ chat: ChatMeta }>(`/api/chats/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }).then((r) => r.chat);
}

export function removeChat(id: string): Promise<unknown> {
  const w = waiting.get(id);
  if (w) clearTimeout(w.timer);
  waiting.delete(id);
  sent.delete(id);
  return api(`/api/chats/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** A chat that is no longer anyone's (signed out): nothing of it is sent. */
export function forgetSaves() {
  for (const w of waiting.values()) clearTimeout(w.timer);
  waiting.clear();
  sent.clear();
  took.clear();
}
